import { useCallback, useState } from 'react';

import {
  CHANNEL,
  getChannelLabel,
  isMetaChannel,
  shouldChannelConnectManually,
} from 'src/data/channel';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import {
  CONNECT_TO_CHANNEL_ORIGIN,
  EMAIL_CSV_RESOURCES,
  isAccountConnected,
  isAccountInProgress,
  PERFORMANCE_EXTRACTION_STATUS,
} from 'src/data/performance';
import { WORKSPACE_ACCESS_LEVEL } from 'src/data/workspace';
import {
  useAddResource,
  useChannelAccounts,
  useCipEvents,
  useErrorHandler,
  usePromptWorkspaceAccessLevel,
} from 'src/services';
import {
  useCustomerActions,
  useIsCurrentUserAdmin,
  useNotificationActions,
  usePerformanceActions,
  useWorkspaceActions,
} from 'src/store';
import { useAccountExtractionLoaderPopup } from 'src/views/AccountExtractionLoaderPopup';
import { useAccountsDataExtractionPopup } from 'src/views/AccountsDataExtractionPopup';
import { useAdAccountsSelectionPopup } from 'src/views/AdAccountsSelectionPopup';
import { useConnectToChannelManuallyPopup } from 'src/views/ConnectToChannelManuallyPopup';
import { useConnectToChannelPopup } from 'src/views/ConnectToChannelPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { useAuthorizeIntegration } from './useAuthorizeIntegration';
import { useRestrictActionPopup } from './useRestrictActionPopup';

export const useConnectToChannel = (trackingEventAttributes = {}) => {
  const notifications = useNotificationActions();
  const customerActions = useCustomerActions();
  const performanceActions = usePerformanceActions();
  const openAdAccountsSelectionPopup = useAdAccountsSelectionPopup();
  const openConnectToChannelManuallyPopup = useConnectToChannelManuallyPopup();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();
  const openRestrictActionPopup = useRestrictActionPopup();
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
  });
  const openConnectToChannelPopup = useConnectToChannelPopup();
  const onAddResource = useAddResource(trackingEventAttributes);
  const { connectedAccounts } = useChannelAccounts();
  const cipEvents = useCipEvents();
  const authorizeIntegration = useAuthorizeIntegration();
  const executeManualConnection = useExecuteManualConnection();

  const [connecting, setConnecting] = useState();

  const connectToChannel = useCallback(
    async ({
      channel,
      brandVoiceId,
      customModel,
      hasEmailConnectedAccounts,
      origin,
      onConnectSuccess,
      isTrainingModel,
      reauthorize,
    }) => {
      const fromIntegrationsPage = origin === CONNECT_TO_CHANNEL_ORIGIN.integrationsAndResources;
      const isChannelConnected = performanceActions.isChannelConnected(channel);
      const isEmailChannel = channel === CHANNEL.emailChannel;
      const hasConnectedAccounts = connectedAccounts[channel]?.length > 0;

      if (isEmailChannel && !hasEmailConnectedAccounts) {
        // Request user to select particular "CSV" channel
        const selectedChannel = await openConnectToChannelPopup({
          brandVoiceId,
          onConnectSuccess,
          displayResourcesTemplates: true,
          title: 'Email',
          subtitle: 'Select one of the options to import email data',
          isTrainingModel,
        });
        if (!selectedChannel) {
          return;
        }

        if (EMAIL_CSV_RESOURCES.includes(selectedChannel)) {
          await onAddResource();
          onConnectSuccess?.();
          return;
        }

        channel = selectedChannel;
      }

      // Check if we need to request "manual" credentials for this channel from user
      let manualCredentials;
      if (
        (shouldChannelConnectManually(channel) ||
          ([
            CHANNEL.salesforceExport,
            CHANNEL.salesforceExactTarget,
            CHANNEL.eloqua,
            CHANNEL.iterable,
          ].includes(channel) &&
            (reauthorize || !isChannelConnected))) &&
        (!hasConnectedAccounts || fromIntegrationsPage || reauthorize)
      ) {
        manualCredentials = await openConnectToChannelManuallyPopup({ channel });
        if (!manualCredentials) {
          return;
        }

        // For any channel other than "salesforceExport" we should continue with "manual" flow here,
        // otherwise, we have manual creds now and should continue
        if (channel !== CHANNEL.salesforceExport) {
          if (customerActions.featureLimitExceeded(FEATURE_LIMITS.resourcesRowsPerCustomer)) {
            openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
            return;
          }

          executeManualConnection({
            credentials: manualCredentials,
            isTrainingModel,
            brandVoiceId,
            onConnectSuccess,
            channel,
            reauthorize,
          });
          return;
        }
      }

      if (
        !(
          isChannelConnected ||
          (isEmailChannel && hasEmailConnectedAccounts) ||
          (isTrainingModel && hasConnectedAccounts)
        ) ||
        reauthorize
      ) {
        if (!isCurrentUserAdmin) {
          openRestrictActionPopup();
          return;
        }

        cipEvents.trackConnectToChannel(channel, origin);
        setConnecting(true);
        try {
          if (isMetaChannel(channel)) {
            await performanceActions.connectToMetaChannel(channel, {
              skipAccountsLoading: reauthorize,
            });
          } else if (
            [CHANNEL.salesforceExport, CHANNEL.salesforceExactTarget, CHANNEL.eloqua].includes(
              channel
            ) &&
            manualCredentials
          ) {
            await performanceActions.integrationAuthorize(
              channel,
              {
                clientId: manualCredentials.clientId,
                clientSecret: manualCredentials.clientSecret,
                identityUrl: manualCredentials.identityUrl,
              },
              {
                skipAccountsLoading: reauthorize,
              }
            );
          } else {
            await authorizeIntegration(channel, {
              skipAccountsLoading: reauthorize,
            });
          }

          cipEvents.trackChannelConnected(channel, origin);
        } catch (error) {
          notifications.displayError(error);
          return;
        } finally {
          setConnecting(false);
        }
      }

      // If this is a reauthorization, the user doesn't need to select ad accounts
      if (reauthorize) {
        notifications.displaySuccess(
          `Integration with ${getChannelLabel(channel)} reauthorized successfully.`
        );
        return;
      }

      // After successful connection, we ask user to select "ad account" to finish the flow
      return openAdAccountsSelectionPopup({
        channel,
        brandVoiceId,
        customModel,
        isTrainingModel,
        onConnectSuccess,
      });
    },
    [
      connectedAccounts,
      performanceActions,
      openAdAccountsSelectionPopup,
      openConnectToChannelPopup,
      onAddResource,
      customerActions,
      openConnectToChannelManuallyPopup,
      openUpgradePopup,
      executeManualConnection,
      isCurrentUserAdmin,
      cipEvents,
      openRestrictActionPopup,
      authorizeIntegration,
      notifications,
    ]
  );

  return {
    connectToChannel,
    connecting,
  };
};

const useExecuteManualConnection = () => {
  const performanceActions = usePerformanceActions();
  const customerActions = useCustomerActions();
  const workspaceActions = useWorkspaceActions();
  const handleError = useErrorHandler();
  const openAccountExtractionLoaderPopup = useAccountExtractionLoaderPopup();
  const openAccountsDataExtractionPopup = useAccountsDataExtractionPopup();
  const promptWorkspaceAccessLevel = usePromptWorkspaceAccessLevel();
  const notifications = useNotificationActions();

  return useCallback(
    async ({
      credentials,
      isTrainingModel,
      brandVoiceId,
      onConnectSuccess,
      channel,
      reauthorize,
    }) => {
      try {
        const account = await performanceActions.integrationAuthorize(channel, credentials);

        // Request workspace access level for this account - only if customer has more than 1 workspace
        let workspaceAccessLevel;
        if (workspaceActions.getCurrentWorkspaces().length > 1) {
          workspaceAccessLevel = await promptWorkspaceAccessLevel(account);
          if (!workspaceAccessLevel) {
            return;
          }
        }

        performanceActions.mergeToAccountList(account.channel, account);

        if (isAccountConnected(account) || isAccountInProgress(account)) {
          if (isAccountConnected(account)) onConnectSuccess?.({ brandVoiceId });

          if (reauthorize)
            notifications.displaySuccess(
              `Integration with ${getChannelLabel(channel)} reauthorized successfully.`
            );
          else
            notifications.displaySuccess(
              `Integration with ${getChannelLabel(channel)} connected successfully.`
            );
          return;
        }

        openAccountExtractionLoaderPopup({
          account,
          isTrainingModel,
        });

        const result = await performanceActions.connectPerformanceCenterAccount({
          account,
          workspaceId:
            workspaceAccessLevel === WORKSPACE_ACCESS_LEVEL.onlyCurrentWorkspace
              ? workspaceActions.getCurrentWorkspaceId()
              : null,
          customModelBrandVoiceId: brandVoiceId,
        });

        const customModelId = result?.customDatasetId;

        if (customModelId) {
          onConnectSuccess?.({
            customModelId,
            brandVoiceId,
          });
        } else {
          if (result?.status === PERFORMANCE_EXTRACTION_STATUS.connected) {
            openAccountsDataExtractionPopup({ account });
            onConnectSuccess?.({
              brandVoiceId,
            });
          }
        }
        customerActions.refreshCurrentCustomerFeatureLimit();
        customerActions.refreshCurrentCustomerRowsAllocation();
      } catch (error) {
        handleError(error);
      }
    },
    [
      customerActions,
      handleError,
      openAccountExtractionLoaderPopup,
      openAccountsDataExtractionPopup,
      performanceActions,
      promptWorkspaceAccessLevel,
      workspaceActions,
      notifications,
    ]
  );
};
